"use client"
import geohash from "ngeohash";
import React from "react";
const turf = require("@turf/turf");
import exifr from "exifr";

function Utils(configArg) {
  const { apiUrl } = configArg;
  if (typeof Utils.instance === "object") {
    return Utils.instance;
  }

  const generateSpacesAlongLine = (
    startPoint,
    endPoint,
    spaceLength,
    spaceWidth,
    bufferDistance,
    orientation,
    unit = "meters"
  ) => {
    const spaces = [];

    // Convert feet to meters if needed
    const conversionFactor = unit === "feet" ? 0.3048 : 1;
    const adjustedSpaceLength = spaceLength * conversionFactor;
    const adjustedSpaceWidth = spaceWidth * conversionFactor;
    const adjustedBufferDistance = bufferDistance * conversionFactor;

    // Create a line segment from the start and end points
    const lineSegment = turf.lineString([startPoint, endPoint]);

    // Calculate the total length of the line segment
    const lineLength = turf.length(lineSegment, { units: "meters" });

    // Calculate the number of spaces that can fit on the line segment
    const numberOfSpaces = Math.floor(lineLength / adjustedSpaceLength);

    // Iterate over the number of spaces to generate each space
    for (let i = 0; i < numberOfSpaces; i++) {
      // Calculate the start point of the space
      const currentStartPoint = turf.along(
        lineSegment,
        i * adjustedSpaceLength,
        { units: "meters" }
      );

      // Calculate the end point of the space
      const currentEndPoint = turf.along(
        lineSegment,
        (i + 1) * adjustedSpaceLength,
        { units: "meters" }
      );

      // Calculate the bearing of the line segment
      const bearing = turf.bearing(currentStartPoint, currentEndPoint);

      // Function to generate the corners of a space on one side of the line
      const generateSpace = (start, end, bufferOffset, facingDirection) => {
        const leftFront = turf.destination(
          start,
          adjustedBufferDistance + bufferOffset,
          bearing + facingDirection,
          { units: "meters" }
        );
        const rightFront = turf.destination(
          start,
          adjustedBufferDistance + adjustedSpaceWidth + bufferOffset,
          bearing + facingDirection,
          { units: "meters" }
        );
        const leftBack = turf.destination(
          end,
          adjustedBufferDistance + bufferOffset,
          bearing + facingDirection,
          { units: "meters" }
        );
        const rightBack = turf.destination(
          end,
          adjustedBufferDistance + adjustedSpaceWidth + bufferOffset,
          bearing + facingDirection,
          { units: "meters" }
        );

        return turf.polygon([
          [
            leftFront.geometry.coordinates,
            rightFront.geometry.coordinates,
            rightBack.geometry.coordinates,
            leftBack.geometry.coordinates,
            leftFront.geometry.coordinates
          ]
        ]);
      };

      // Based on orientation, generate spaces
      if (orientation === "NW") {
        spaces.push(generateSpace(currentStartPoint, currentEndPoint, 0, -90));
      } else if (orientation === "SE") {
        spaces.push(generateSpace(currentStartPoint, currentEndPoint, 0, 90));
      } else if (orientation === "F2F") {
        spaces.push(generateSpace(currentStartPoint, currentEndPoint, 0, -90)); // North or West facing
        spaces.push(generateSpace(currentStartPoint, currentEndPoint, 0, 90)); // South or East facing
      } else if (orientation === "B2B") {
        spaces.push(
          generateSpace(
            currentStartPoint,
            currentEndPoint,
            -adjustedSpaceWidth,
            -90
          )
        ); // North or West facing, away from each other
        spaces.push(generateSpace(currentStartPoint, currentEndPoint, 0, 90)); // South or East facing, away from each other
      } else if (orientation === "BOTH") {
        // Combine Front to Front and Back to Back
        spaces.push(generateSpace(currentStartPoint, currentEndPoint, 0, -90)); // North or West facing (Front to Front)
        spaces.push(generateSpace(currentStartPoint, currentEndPoint, 0, 90)); // South or East facing (Front to Front)
        spaces.push(
          generateSpace(
            currentStartPoint,
            currentEndPoint,
            -adjustedSpaceWidth,
            -90
          )
        ); // Back to Back North or West
        spaces.push(
          generateSpace(
            currentStartPoint,
            currentEndPoint,
            adjustedSpaceWidth,
            90
          )
        ); // Back to Back South or East
      }
    }

    return spaces;
  };

  // // Function to generate spaces along a line segment defined by start and end points
  // const generateSpacesAlongLine = (startPoint, endPoint, spaceLength, spaceWidth, bufferDistance, orientation, unit = "meters") =>
  // {
  //   const spaces = [];
  //
  //   // Convert feet to meters if needed
  //   const conversionFactor = unit === "feet" ? 0.3048 : 1;
  //   const adjustedSpaceLength = spaceLength * conversionFactor;
  //   const adjustedSpaceWidth = spaceWidth * conversionFactor;
  //   const adjustedBufferDistance = bufferDistance * conversionFactor;
  //
  //   // Create a line segment from the start and end points
  //   const lineSegment = turf.lineString([startPoint, endPoint]);
  //
  //   // Calculate the total length of the line segment
  //   const lineLength = turf.length(lineSegment, {units: "meters"});
  //   console.log("lineLength", lineLength);
  //
  //   // Calculate the number of spaces that can fit on the line segment
  //   const numberOfSpaces = Math.floor(lineLength / adjustedSpaceLength);
  //   console.log("numberOfSpaces", numberOfSpaces);
  //
  //   // Iterate over the number of spaces to generate each space
  //   for (let i = 0; i < numberOfSpaces; i++) {
  //     // Calculate the start point of the space
  //     const currentStartPoint = turf.along(lineSegment, i * adjustedSpaceLength, {units: "meters"});
  //
  //     // Calculate the end point of the space
  //     const currentEndPoint = turf.along(lineSegment, (i + 1) * adjustedSpaceLength, {units: "meters"});
  //
  //     // Calculate the bearing of the line segment
  //     const bearing = turf.bearing(currentStartPoint, currentEndPoint);
  //
  //     // Generate the four corners of the space
  //     const leftFrontCorner = turf.destination(currentStartPoint, adjustedBufferDistance, bearing - 90, {units: "meters"});
  //     const rightFrontCorner = turf.destination(currentStartPoint, adjustedBufferDistance + adjustedSpaceWidth, bearing - 90, {units: "meters"});
  //     const leftBackCorner = turf.destination(currentEndPoint, adjustedBufferDistance, bearing - 90, {units: "meters"});
  //     const rightBackCorner = turf.destination(currentEndPoint, adjustedBufferDistance + adjustedSpaceWidth, bearing - 90, {units: "meters"});
  //
  //     // Adjust corners based on orientation
  //     let corners;
  //     if (orientation === "back-to-back") {
  //       corners = [
  //         turf.destination(leftFrontCorner, adjustedSpaceWidth / 2, bearing, {units: "meters"}),
  //         turf.destination(rightFrontCorner, adjustedSpaceWidth / 2, bearing, {units: "meters"}),
  //         turf.destination(rightBackCorner, adjustedSpaceWidth / 2, bearing, {units: "meters"}),
  //         turf.destination(leftBackCorner, adjustedSpaceWidth / 2, bearing, {units: "meters"})
  //       ];
  //     } else if (orientation === "front-to-front") {
  //       corners = [leftFrontCorner, rightFrontCorner, rightBackCorner, leftBackCorner];
  //     } else {
  //       corners = [leftFrontCorner, rightFrontCorner, rightBackCorner, leftBackCorner];
  //     }
  //
  //     // Create the polygon for the space
  //     const spacePolygon = turf.polygon([[
  //       corners[0].geometry.coordinates,
  //       corners[1].geometry.coordinates,
  //       corners[2].geometry.coordinates,
  //       corners[3].geometry.coordinates,
  //       corners[0].geometry.coordinates
  //     ]]);
  //
  //     // Add the space polygon to the array
  //     spaces.push(spacePolygon);
  //   }
  //
  //   return spaces;
  // }

  const distanceFunc = (meters, units = "miles") => {
    if (units === "miles") {
      var distanceInFeet = meters * 3.28084;
      if (distanceInFeet > 5280) {
        return `${Math.floor(distanceInFeet / 5280.0)} mi`;
      } else {
        return `${Math.floor(distanceInFeet)} feet`;
      }
    } else {
      if (meters < 1000) {
        return `${Math.floor(meters)} m`;
      } else {
        return `${Math.floor(meters / 1000.0)} km`;
      }
    }
  };

  const splitCountryFromAddress = str => {
    var split = str.split(",");
    if (split.length > 1) {
      split.splice(-1);
      return split.join(",");
    } else {
      return split[0];
    }
  };

  const computeMapBoundsFromFeature = coordinates => {
    if (coordinates) {
      var minLon = Math.min(...coordinates.map(c => c[0]));
      var maxLon = Math.max(...coordinates.map(c => c[0]));
      var minLat = Math.min(...coordinates.map(c => c[1]));
      var maxLat = Math.max(...coordinates.map(c => c[1]));
      return [
        [minLon, minLat],
        [maxLon, maxLat]
      ];
    } else {
      return null;
    }
  };

  const computeBoundingBoxFromPoints = points => {
    const coords = (points || [])
      .map(p =>
        Array.isArray(p)
          ? p
          : p?.longitude != null && p?.latitude != null
          ? [p.longitude, p.latitude]
          : null
      )
      .filter(Boolean);
    if (coords.length === 0) return null;
    const lons = coords.map(c => c[0]);
    const lats = coords.map(c => c[1]);
    return [
      [Math.min(...lons), Math.min(...lats)],
      [Math.max(...lons), Math.max(...lats)]
    ];
  };

  const computeCenterFromLatLng = points => {
    var minLat = -90.0;
    var minLon = -180.0;
    var maxLat = 90.0;
    var maxLon = 180.0;

    points.forEach(function(point) {
      minLat = Math.max(minLat, point.latitude);
      minLon = Math.max(minLon, point.longitude);
      maxLat = Math.min(maxLat, point.latitude);
      maxLon = Math.min(maxLon, point.longitude);
    });

    let centerLat = (minLat + maxLat) / 2.0;
    let centerLon = (minLon + maxLon) / 2.0;

    return { latitude: centerLat, longitude: centerLon };
  };

  const createFitBounds = (
    locations,
    padding = { top: 40, bottom: 40, left: 40, right: 40 }
  ) => {
    var minLat = -90.0;
    var minLon = -180.0;
    var maxLat = 90.0;
    var maxLon = 180.0;

    locations.forEach(function(location) {
      minLat = Math.max(minLat, location.latitude);
      minLon = Math.max(minLon, location.longitude);
      maxLat = Math.min(maxLat, location.latitude);
      maxLon = Math.min(maxLon, location.longitude);
    });

    return {
      fitBounds: [
        [minLon, minLat],
        [maxLon, maxLat]
      ],
      fitBoundsOptions: { padding }
    };
  };

  const roundTo = (num, decimals) => {
    const factor = Math.pow(10, decimals);
    return +(Math.round(num * factor) + "e-" + decimals);
  };

  const percentLabel = (numerator, denominator, decimals = 2) => {
    if (denominator === 0) {
      return "";
    } else {
      return `${roundTo((numerator / denominator) * 100, decimals)}%`;
    }
  };

  const markerUrl = (color, icon, shape) => {
    var path = `${apiUrl}/api/markers/${color ?? "000000"}${icon ? `/${icon}` : "/NONE"}${
      shape ? `/${shape}` : "/PIN"
    }`;
    return path;
  };

  const arraysIntersect = (arr1, arr2) => {
    return arr1.some(item => arr2.includes(item));
  };

  const loadDynamicCss = url => {
    const link = document.createElement("link");
    link.href = url;
    link.type = "text/css";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  };

  const buildUserLocation = (latitude, longitude) => {
    var resourceName = geohash.encode(latitude, longitude);
    var locationName = `${latitude},${longitude} (from user)`;
    var location = {
      name: locationName,
      address: "",
      latitude: latitude,
      longitude: longitude,
      locationSource: "USER",
      resourceName: resourceName,
      externalId: resourceName
    };
    return location;
  };

  const convertObjectToQueryString = params => {
    // if params is null or params object is empty, return empty string
    if (!params || Object.keys(params).length === 0) {
      return "";
    } else {
      return (
        "?" +
        Object.keys(params)
          .map(key => key + "=" + params[key])
          .join("&")
      );
    }
  };

  const pointOfContactLabel = poc => {
    if (poc.firstName || poc.lastName) {
      return `${
        poc.firstName != null && poc.firstName !== ""
          ? poc.firstName
          : "<First>"
      } ${
        poc.lastName != null && poc.lastName !== "" ? poc.lastName : "<Last>"
      }`;
    } else {
      return "No name on file";
    }
  };

  const randomString = n => {
    // Define the list of characters
    let alphaNumericString = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

    // The variable to store the random string
    let result = "";

    // Get the length of the characters list
    let charactersLength = alphaNumericString.length;

    // Generate a random string of length n
    for (let i = 0; i < n; i++) {
      result += alphaNumericString.charAt(
        Math.floor(Math.random() * charactersLength)
      );
    }

    return result;
  };
  const buildCustomFields = object => {
    // console.log("customFields", object);
    if (
      object &&
      ((object["stringCustomFields"] &&
        Object.keys(object["stringCustomFields"]).length > 0) ||
        (object["numberCustomFields"] &&
          Object.keys(object["numberCustomFields"]).length > 0) ||
        (object["doubleCustomFields"] &&
          Object.keys(object["doubleCustomFields"]).length > 0) ||
        (object["booleanCustomFields"] &&
          Object.keys(object["booleanCustomFields"]).length > 0))
    ) {
      return (
        <>
          {object["stringCustomFields"] &&
            Object.keys(object["stringCustomFields"]).length > 0 && (
              <>
                {Object.keys(object["stringCustomFields"]).map((key, index) => {
                  return (
                    <p>
                      <b>{key}:</b>&nbsp;
                      {object["stringCustomFields"][key] ?? "Not Provided"}
                    </p>
                  );
                })}
              </>
            )}
          {object["numberCustomFields"] &&
            Object.keys(object["numberCustomFields"]).length > 0 && (
              <>
                {Object.keys(object["numberCustomFields"]).map((key, index) => {
                  return (
                    <p>
                      <b>{key}:</b>&nbsp;
                      {object["numberCustomFields"][key] ?? "Not Provided"}
                    </p>
                  );
                })}
              </>
            )}
          {object["doubleCustomFields"] &&
            Object.keys(object["doubleCustomFields"]).length > 0 && (
              <>
                {Object.keys(object["doubleCustomFields"]).map((key, index) => {
                  return (
                    <p>
                      <b>{key}:</b>&nbsp;
                      {object["doubleCustomFields"][key] ?? "Not Provided"}
                    </p>
                  );
                })}
              </>
            )}
          {object["booleanCustomFields"] &&
            Object.keys(object["booleanCustomFields"]).length > 0 && (
              <>
                {Object.keys(object["booleanCustomFields"]).map(
                  (key, index) => {
                    return (
                      <p>
                        <b>{key}:</b>&nbsp;
                        {object["booleanCustomFields"][key]
                          ? object["booleanCustomFields"][key]
                            ? "Yes"
                            : "No"
                          : "Not Provided"}
                      </p>
                    );
                  }
                )}
              </>
            )}
        </>
      );
    } else {
      return <></>;
    }
  };

  const getExifData = async file => {
    // console.log("getExifData in", file);
    var temp = await exifr.parse(file.src.file);
    var rotation = await exifr.rotation(file.src.file);
    var location = await exifr.gps(file.src.file);
    // console.log( "getExifData", temp, rotation, location);
    var result = refineExifData(temp, file.id);
    result.rotation = rotation.deg ? rotation.deg : 0;
    result.latitude = location ? location.latitude : null;
    result.longitude = location ? location.longitude : null;

    return result;
  };

  const refineExifData = (data, id) => {
    let usefulData = ["ImageWidth", "ImageHeight", "CreateDate"];
    let result = {
      id: id
    };
    let lowercased = s => {
      return s.charAt(0).toLowerCase() + s.slice(1);
    };

    usefulData.map(key => {
      if (data && data[key]) {
        result[lowercased(key)] = data[key];
      } else if (data && data["Exif" + key]) {
        result[lowercased(key)] = data["Exif" + key];
      } else {
        result[lowercased(key)] = null;
      }
    });

    if (result.imageWidth > 1600 || result.imageHeight > 1600) {
      let width = result.imageWidth;
      let height = result.imageHeight;
      let maxSide = Math.max(width, height);
      let reduceRatio = maxSide / 1600;
      result.width = Math.round(width / reduceRatio);
      result.height = Math.round(height / reduceRatio);
    }
    return result;
  };

  const formatPhoneNumber = phone => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, "");

    if (digits.length === 11 && digits.startsWith("1")) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(
        7,
        11
      )}`;
    }

    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
        6,
        10
      )}`;
    }

    return phone;
  };

  const sanitizePhoneNumber = phone => {
    if (!phone) return null;
    const trimmed = phone.trim();
    if (!trimmed) return null;

    const sanitized = trimmed.replace(/[^0-9+]/g, "");
    if (!sanitized) return null;

    if (sanitized.startsWith("+")) {
      return sanitized;
    }

    if (sanitized.length === 11 && sanitized.startsWith("1")) {
      return `+${sanitized}`;
    }

    if (sanitized.length === 10) {
      return `+1${sanitized}`;
    }

    return sanitized;
  };

  // const formatPhoneNumber = phoneNumber => {
  //   var cleaned = ("" + phoneNumber).replace(/\D/g, "");
  //   if (cleaned.length === 11 && cleaned.startsWith("1")) {
  //     cleaned = cleaned.substring(1);
  //   }
  //   if (cleaned.length === 10) {
  //     return `(${cleaned.substring(0, 3)}) ${cleaned.substring(
  //       3,
  //       6
  //     )}-${cleaned.substring(6)}`;
  //   } else {
  //     return cleaned; // fallback to original if not 10 digits
  //   }
  // };

  Utils.instance = Object.freeze({
    distanceFunc,
    splitCountryFromAddress,
    computeMapBoundsFromFeature,
    computeBoundingBoxFromPoints,
    computeCenterFromLatLng,
    createFitBounds,
    roundTo,
    percentLabel,
    markerUrl,
    arraysIntersect,
    loadDynamicCss,
    buildUserLocation,
    convertObjectToQueryString,
    pointOfContactLabel,
    randomString,
    buildCustomFields,
    generateSpacesAlongLine,
    getExifData,
    refineExifData,
    formatPhoneNumber,
    sanitizePhoneNumber
  });
  return Utils.instance;
}
export default Utils;
